import type { OnboardingState, OperatorClass, Person, PresenceDay, RemovedRoleDto, Role } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { humanize, todayIso } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, Select } from '../../ui';
import { PeopleImport } from './PeopleImport';
import { useSites } from './SiteScope';

const CLASS_LABEL: Record<OperatorClass, string> = { knowledge_worker: 'Knowledge worker', linea_operativa: 'Linea operativa' };

function RolePicker({ roles, value, onChange }: { roles: Role[]; value: string[]; onChange: (v: string[]) => void }) {
  const [custom, setCustom] = useState('');
  const toggle = (k: string) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  const keys = [...new Set([...roles.map((r) => r.key), ...value])];
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-ink/80">Ruoli</span>
      <div className="flex flex-wrap gap-2">
        {keys.map((k) => (
          <button key={k} type="button" onClick={() => toggle(k)} className={`rounded-full border px-3 py-1 text-xs ${value.includes(k) ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}>
            {humanize(k)}
          </button>
        ))}
        <input
          className="w-36 rounded-full border border-dashed border-black/20 px-3 py-1 text-xs outline-none"
          placeholder="nuovo ruolo + Invio"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            const k = custom.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            if (k && /^[a-z]/.test(k) && !value.includes(k)) onChange([...value, k]);
            setCustom('');
          }}
        />
      </div>
    </div>
  );
}

/** Ruoli tolti dalla verifica (doppioni, figure che l'azienda non ha o di un solo progetto): si possono ripristinare. */
function RemovedRoles({ onRestored }: { onRestored: () => void }) {
  const { t } = useTenant();
  const removed = useTenantQuery<{ data: RemovedRoleDto[] }>(['roles-removed'], '/onboarding/roles/removed');
  const restore = useMutation({
    mutationFn: (key: string) => api<{ data: RemovedRoleDto[] }>(t(`/onboarding/roles/${encodeURIComponent(key)}/restore`), { method: 'POST' }),
    onSuccess: onRestored,
  });
  const list = removed.data?.data ?? [];
  if (!list.length) return null;
  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold">Ruoli tolti</h2>
      <p className="mb-3 text-xs text-ink/55">Il sistema li ha tolti perché doppioni, figure che non avete o legate a un solo progetto. Se servono, ripristinali: non verranno più tolti.</p>
      <ErrorText>{restore.error ? errorMessage(restore.error) : ''}</ErrorText>
      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.chiave} className="text-sm">
            <p className="font-medium">{r.nome}</p>
            {r.motivo && <p className="text-xs text-ink/55">{r.motivo}</p>}
            {r.sostituito_da && <p className="text-xs text-ink/55">Unito in: {humanize(r.sostituito_da)} (persone e procedure restano lì).</p>}
            <button className="mt-1 text-xs text-accent hover:underline disabled:opacity-50" disabled={restore.isPending} onClick={() => restore.mutate(r.chiave)}>
              Ripristina
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function People({ importing = false }: { importing?: boolean }) {
  const { tenant, t, isManager, me } = useTenant();
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const people = useTenantQuery<{ data: Person[] }>(['people'], '/people');
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles');
  const day = todayIso();
  const presence = useTenantQuery<PresenceDay>(['presence', day], `/presence?day=${day}`);

  const [importOpen, setImportOpen] = useState(importing);
  const [name, setName] = useState('');
  const [personRoles, setPersonRoles] = useState<string[]>([]);
  const [klass, setKlass] = useState<OperatorClass>('linea_operativa');
  const [linkMe, setLinkMe] = useState(false);
  const [phone, setPhone] = useState('');
  const [shared, setShared] = useState<{ personId: string; url: string; whatsapp_url: string | null } | null>(null);
  const share = useMutation({
    mutationFn: (personId: string) => api<{ url: string; whatsapp_url: string | null }>(t(`/people/${personId}/line-link`), { method: 'POST' }),
    onSuccess: (r, personId) => setShared({ personId, ...r }),
  });
  const create = useMutation({
    mutationFn: () => api<Person>(t('/people'), { body: { name, roles: personRoles, operator_class: klass, link_me: linkMe, ...(phone.trim() ? { phone: phone.trim() } : {}) } }),
    onSuccess: () => {
      setName('');
      setPhone('');
      setPersonRoles([]);
      setLinkMe(false);
      refresh();
    },
  });
  const update = useMutation({ mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api<Person>(t(`/people/${id}`), { method: 'PATCH', body }), onSuccess: refresh });

  const [marks, setMarks] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (presence.data) setMarks(Object.fromEntries(presence.data.data.map((p) => [p.person_id, p.present ?? false])));
  }, [presence.data]);
  const savePresence = useMutation({
    mutationFn: () => api<PresenceDay>(t('/presence'), { method: 'PUT', body: { day, entries: Object.entries(marks).map(([person_id, present]) => ({ person_id, present })) } }),
    onSuccess: refresh,
  });

  const [areasFor, setAreasFor] = useState<string | null>(null);
  const [sitesFor, setSitesFor] = useState<string | null>(null);
  const sites = useSites();
  const [memberEmail, setMemberEmail] = useState('');
  const addMember = useMutation({ mutationFn: () => api(t('/members'), { body: { email: memberEmail } }), onSuccess: () => setMemberEmail('') });

  const linked = people.data?.data.some((p) => p.user_id === me.id);
  const byId = new Map((people.data?.data ?? []).map((p) => [p.id, p.name]));
  const roleLabel = new Map((roles.data?.data ?? []).map((r) => [r.key, r.label]));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const error = create.error ?? update.error ?? savePresence.error ?? addMember.error ?? share.error;

  return (
    <>
      <PageTitle
        title="Persone"
        subtitle="Chi lavora, con quali ruoli: il motore di assegnazione sceglie tra loro per ruolo, presenza e carico."
        actions={isManager && !importOpen ? <Button variant="ghost" onClick={() => setImportOpen(true)}>Importa persone e ruoli</Button> : undefined}
      />
      <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-6">
          {isManager && importOpen && <PeopleImport onClose={() => setImportOpen(false)} />}
          {isManager && (
            <Card>
              <h2 className="mb-4 text-sm font-semibold">Aggiungi una persona</h2>
              <form className="space-y-4" onSubmit={submit}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Nome" value={name} onChange={(e) => setName(e.target.value)} required />
                  <Select label="Classe" value={klass} onChange={(e) => setKlass(e.target.value as OperatorClass)}>
                    <option value="linea_operativa">Linea operativa (task su canale minimo)</option>
                    <option value="knowledge_worker">Knowledge worker (assistente AI, dalla Fase 4)</option>
                  </Select>
                </div>
                <Field label="Telefono (facoltativo, per inviare il link su WhatsApp)" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                <RolePicker roles={roles.data?.data ?? []} value={personRoles} onChange={setPersonRoles} />
                {!linked && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={linkMe} onChange={(e) => setLinkMe(e.target.checked)} /> Sono io (collega al mio account per vedere i miei task)
                  </label>
                )}
                <Button type="submit" disabled={create.isPending}>
                  Aggiungi
                </Button>
              </form>
            </Card>
          )}
          <Card className="py-2">
            {people.data?.data.length === 0 && <Empty>Nessuna persona.</Empty>}
            <div className="divide-y divide-black/5">
              {people.data?.data.map((p) => (
                <div key={p.id} className={`flex flex-wrap items-center gap-3 py-3 ${p.active ? '' : 'opacity-50'}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {p.name} {p.user_id === me.id && <Badge tone="blue">tu</Badge>}
                    </p>
                    <p className="text-xs text-ink/55">
                      {p.roles.map((r) => roleLabel.get(r) ?? humanize(r)).join(', ') || 'nessun ruolo'}
                      {p.department && ` · ${p.department}`}
                      {p.manager_id && byId.get(p.manager_id) && ` · responsabile ${byId.get(p.manager_id)}`} · {CLASS_LABEL[p.operator_class]} · {p.open_tasks} task aperti
                      {p.source !== 'manuale' && <span className="text-ink/40"> · importata</span>}
                    </p>
                    {p.procedure_areas.length > 0 && (
                      <p className="mt-1 text-xs text-accent">Responsabile delle procedure: {p.procedure_areas.join(', ')}</p>
                    )}
                    {sites.multi && (
                      <p className={`mt-1 text-xs ${p.all_sites || p.sites.length ? 'text-ink/60' : 'text-amber-700'}`}>
                        Sede: {p.all_sites ? 'tutte le sedi' : p.sites.length ? p.sites.map((x) => `${sites.name(x.site_id)}${x.roles.length ? ` (${x.roles.map((r) => roleLabel.get(r) ?? humanize(r)).join(', ')})` : ''}`).join(', ') : 'non indicata'}
                      </p>
                    )}
                  </div>
                  {isManager && p.active && !p.user_id && (
                    <Button variant="ghost" onClick={() => share.mutate(p.id)} title="Link personale alla pagina dei suoi compiti, senza account">
                      Link compiti
                    </Button>
                  )}
                  {isManager && p.active && sites.multi && (
                    <Button variant="ghost" onClick={() => setSitesFor(sitesFor === p.id ? null : p.id)} title="In quali sedi lavora: i compiti di una sede vanno solo a chi ci lavora">
                      Sedi
                    </Button>
                  )}
                  {isManager && p.active && (
                    <Button variant="ghost" onClick={() => setAreasFor(areasFor === p.id ? null : p.id)} title="Aree della pagina Procedure che questa persona può verificare, cambiare e completare">
                      Procedure
                    </Button>
                  )}
                  {isManager && (
                    <Button variant="ghost" onClick={() => update.mutate({ id: p.id, body: { active: !p.active } })}>
                      {p.active ? 'Disattiva' : 'Riattiva'}
                    </Button>
                  )}
                  {areasFor === p.id && <ProcedureAreasEditor person={p} onDone={() => { setAreasFor(null); refresh(); }} />}
                  {sitesFor === p.id && <PersonSitesEditor person={p} onDone={() => { setSitesFor(null); refresh(); }} />}
                  {shared?.personId === p.id && (
                    <div className="w-full rounded-lg bg-black/[0.03] p-3 text-sm">
                      <p className="mb-2 text-ink/70">Link personale di {p.name}: apre la pagina dei suoi compiti, senza account. Generarne uno nuovo disattiva il precedente.</p>
                      <code className="block truncate text-xs">{shared.url}</code>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button variant="ghost" onClick={() => void navigator.clipboard?.writeText(shared.url)}>
                          Copia link
                        </Button>
                        {shared.whatsapp_url && (
                          <a className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white" href={shared.whatsapp_url} target="_blank" rel="noreferrer">
                            Invia su WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="space-y-6">
          <Card>
            <h2 className="mb-1 text-sm font-semibold">Presenze di oggi</h2>
            <p className="mb-3 text-xs text-ink/55">Spunta manuale. Chi è assente non riceve nuovi task.</p>
            <div className="space-y-1">
              {presence.data?.data.map((p) => (
                <label key={p.person_id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" disabled={!isManager} checked={marks[p.person_id] ?? false} onChange={(e) => setMarks({ ...marks, [p.person_id]: e.target.checked })} />
                  {p.name}
                  {p.present === null && <span className="text-xs text-ink/40">(non rilevata)</span>}
                </label>
              ))}
            </div>
            {isManager && !!presence.data?.data.length && (
              <Button className="mt-3" variant="ghost" disabled={savePresence.isPending} onClick={() => savePresence.mutate()}>
                Salva presenze
              </Button>
            )}
          </Card>
          {isManager && <RemovedRoles onRestored={refresh} />}
          {isManager && (
            <Card>
              <h2 className="mb-1 text-sm font-semibold">Accesso di altri utenti</h2>
              <p className="mb-3 text-xs text-ink/55">L’utente deve aver già creato un account. Vedrà e chiuderà i propri task.</p>
              <div className="flex gap-2">
                <input className="min-w-0 flex-1 rounded-lg border border-black/15 px-3 py-2 text-sm" type="email" placeholder="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} />
                <Button variant="ghost" disabled={!memberEmail || addMember.isPending} onClick={() => addMember.mutate()}>
                  Aggiungi
                </Button>
              </div>
              {addMember.isSuccess && <p className="mt-2 text-xs text-emerald-700">Utente aggiunto come membro.</p>}
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Responsabile delle procedure: le aree della pagina Procedure che la persona può verificare, cambiare e completare
 * con il suo account (quello della sua email, aggiunto all'azienda se serve).
 */
function ProcedureAreasEditor({ person, onDone }: { person: Person; onDone: () => void }) {
  const { t } = useTenant();
  const state = useTenantQuery<OnboardingState>(['onboarding'], '/onboarding');
  const [chosen, setChosen] = useState<string[]>(person.procedure_areas);
  const [email, setEmail] = useState(person.email ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<Person>(t(`/people/${person.id}`), {
        method: 'PATCH',
        body: { procedure_areas: chosen, ...(!person.user_id && email.trim() && email.trim() !== person.email ? { email: email.trim() } : {}) },
      }),
    onSuccess: onDone,
  });
  const areas = state.data?.areas.map((a) => a.area) ?? [];
  const toggle = (a: string) => setChosen(chosen.includes(a) ? chosen.filter((x) => x !== a) : [...chosen, a]);
  return (
    <div className="w-full rounded-lg bg-black/[0.03] p-3 text-sm">
      <p className="mb-2 text-ink/70">
        Aree di cui {person.name} è responsabile: con il suo account le vede nella pagina Procedure, le verifica, le cambia e ne aggiunge, solo lì.
      </p>
      {state.isLoading && <p className="text-xs text-ink/50">Carico le aree…</p>}
      {state.data && !areas.length && <p className="text-xs text-ink/50">Le aree compaiono quando la mappa delle procedure è pronta.</p>}
      <div className="mb-3 flex flex-wrap gap-2">
        {areas.map((a) => (
          <button key={a} type="button" onClick={() => toggle(a)} className={`rounded-full border px-3 py-1 text-xs ${chosen.includes(a) ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}>
            {a}
          </button>
        ))}
      </div>
      {!person.user_id && chosen.length > 0 && (
        <Field label="Email del suo account (deve essersi registrata con questa email)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      )}
      <div className="mt-2 flex gap-2">
        <Button disabled={save.isPending || (!person.user_id && chosen.length > 0 && !email.includes('@'))} onClick={() => save.mutate()}>
          Salva
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}

/**
 * Dove lavora una persona (ADR 0017): su tutte le sedi (es. una direzione) o su alcune, con i ruoli che vi svolge
 * (nessun ruolo scelto = tutti i suoi). I compiti di una sede vanno solo a chi ci lavora.
 */
function PersonSitesEditor({ person, onDone }: { person: Person; onDone: () => void }) {
  const { t } = useTenant();
  const { sites } = useSites();
  const [all, setAll] = useState(person.all_sites);
  const [chosen, setChosen] = useState<Record<string, string[]>>(Object.fromEntries(person.sites.map((x) => [x.site_id, x.roles])));
  const save = useMutation({
    mutationFn: () => api(t(`/people/${person.id}/sites`), { method: 'PUT', body: { tutte: all, sedi: all ? [] : Object.entries(chosen).map(([site_id, roles]) => ({ site_id, roles })) } }),
    onSuccess: onDone,
  });
  const toggleSite = (id: string) => {
    const next = { ...chosen };
    if (id in next) delete next[id];
    else next[id] = [];
    setChosen(next);
  };
  const toggleRole = (id: string, r: string) => setChosen({ ...chosen, [id]: chosen[id]!.includes(r) ? chosen[id]!.filter((x) => x !== r) : [...chosen[id]!, r] });
  return (
    <div className="w-full rounded-lg bg-black/[0.03] p-3 text-sm">
      <p className="mb-2 text-ink/70">In quali sedi lavora {person.name}? Riceve compiti e indicazioni solo di quelle sedi (e di ciò che vale per tutta l’azienda).</p>
      <label className="mb-2 flex items-center gap-2">
        <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Su tutte le sedi
      </label>
      {!all &&
        sites.map((x) => (
          <div key={x.id} className="mb-2">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={x.id in chosen} onChange={() => toggleSite(x.id)} /> {x.name}
            </label>
            {x.id in chosen && person.roles.length > 1 && (
              <div className="ml-6 mt-1 flex flex-wrap gap-1">
                <span className="text-xs text-ink/50">ruoli qui (nessuno = tutti):</span>
                {person.roles.map((r) => (
                  <button key={r} type="button" onClick={() => toggleRole(x.id, r)} className={`rounded-full border px-2 py-0.5 text-xs ${chosen[x.id]!.includes(r) ? 'border-accent bg-accent text-white' : 'border-black/15 text-ink/70'}`}>
                    {humanize(r)}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      <div className="mt-2 flex gap-2">
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Salva
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}
